import React from 'react';
import './splitImageBodyPanel.css';
import { Badge, Label, Icon } from '@nooz-optics/nooz-components-lib';
import { Body } from '../../../../UI/Elements';
import { Image } from '../../../../UI/Media/Image';
import { CrmForm } from '../../../../Admin/Prospect';
import { formatContentfulComponentProps } from '../../../../../utils/globals';
import { Grid } from '../../../../UI/Grid/grid';
import { Video } from '../../../../UI/Media';
import { BannerButtons } from '../../../Banners';
import { Link } from '../../../../UI/Elements/Link';
import clsx from 'clsx';

export const SplitImageBodyPanel = ({
  body,
  imageFirst = { value: false },
  image,
  icon,
}) => {
  const isImageFirst = imageFirst?.value;
  return (
    <div
      id='split-panel'
      className='relative flex overflow-scroll no-scrollbar flex-col w-full h-full'
    >
      <div className='relative z-10 py-8 h-full w-full flex flex-col justify-center sm:px-6 lg:px-8 lg:max-w-7xl lg:mx-auto  lg:grid lg:grid-cols-2'>
        {/* Left empty Div for image spacer on the left */}
        {isImageFirst && <div />}
        <div
          className={`${
            isImageFirst ? 'lg:pl-8' : 'lg:pr-8'
          } flex flex-col justify-center h-auto text-left`}
        >
          {icon && (
            <div className='w-20'>
              <Icon {...icon} />
            </div>
          )}
          {body && (
            <div>
              <Body {...body} />
            </div>
          )}
        </div>
        {/* Right empty Div for image spacer on the right */}
        {isImageFirst && <div />}
      </div>
      {image && (
        <div className='lg:absolute py-6 lg:inset-0'>
          <div
            className={`flex lg:absolute lg:inset-y-0 lg:${
              isImageFirst ? 'left-0' : 'right-0'
            } lg:w-1/2`}
          >
            <Image {...image} />
          </div>
        </div>
      )}
    </div>
  );
};

// Image and CRM Form Panel
export const SplitMediaCrmFormPanel = ({
  badges,
  body,
  buttons,
  color,
  desktopImage,
  form,
  image,
  label,
  link,
  richBody,
  video,
}) => (
  <div
    style={{ background: color ? color?.hex : '' }}
    className={`relative ${
      color ? '' : 'bg-white'
    } w-full lg:flex flex-row-reverse`}
  >
    <div className='pt-5 lg:pt-0 w-full h-full lg:flex lg:items-center'>
      <div className='relative main-wrapper py-16 px-4 sm:py-24 sm:px-6 lg:px-8 lg:mx-auto lg:py-16 lg:grid lg:grid-cols-2'>
        <div className='w-full lg:pr-4'>
          {badges?.items?.map((badge) => (
            <div className='mr-2 pb-5'>
              <Badge {...badge} />
            </div>
          ))}
          {label && (
            <div className='text-left pb-5'>
              <Label {...label} />
            </div>
          )}
          {body && (
            <div className='text-left pb-5'>
              <Label {...body} />
            </div>
          )}
          {richBody && (
            <div className='text-left pb-5'>
              <Body {...richBody} />
            </div>
          )}
          {buttons && (
            <div className='py-5'>
              <BannerButtons buttons={buttons} size='lg' />
            </div>
          )}
          {form && (
            <div className='py-5 lg:w-80'>
              <CrmForm form={form} />
            </div>
          )}
          {link && (
            <div className='underline text-left pb-5 lg:pb-0'>
              <Link {...link} />
            </div>
          )}
        </div>
        <div />
      </div>
    </div>
    <div />
    <div
      className={clsx(
        'relative split-image-crm lg:h-full h-min block lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2',
        { ['split-image-crm-video']: !!video }
      )}
    >
      {desktopImage && (
        <div className='hidden lg:h-full lg:block'>
          <Image
            {...desktopImage}
            height='100%'
            width='100%'
            objectFit='cover'
          />
        </div>
      )}
      {image && (
        <div className='absolute h-full lg:hidden'>
          <Image {...image} height='100%' width='100%' />
        </div>
      )}
      {video && (
        <div className='absolute inset-0'>
          <Video {...video} height='100%' width='100%' />
        </div>
      )}
    </div>
  </div>
);

// Image Slider and Body Panel
export const SplitSliderImagesBodyPanel = ({ images, body, hasBorder }) => (
  <div
    className={`split-image-body-panel bg-white relative overflow-hidden border flex flex-wrap w-full ${
      hasBorder ? 'border' : ''
    }`}
  >
    <div className='w-full h-auto flex justify-center items-center lg:w-1/2'>
      <div className='px-12 lg:px-20 py-16 sm:py-24 h-full flex flex-col justify-center'>
        <Body {...body} />
      </div>
    </div>
    <div className='right-0 flex items-center overflow-hidden w-full h-full md:max-h-96 lg:max-h-full lg:w-1/2'>
      <Grid
        grid={{
          wrap: true,
          isSlider: 'block',
          itemsWidth: 'min-w-full',
          gridItems: images.items.map((image) => (
            <div className='flex items-center w-full h-full justify-center'>
              <Image
                {...image}
                isNative
                objectFit='cover'
                height='100%'
                width='100%'
              />
            </div>
          )),
        }}
      />
    </div>
  </div>
);

export const SplitImageBodyDetailsPanel = ({
  image,
  body,
  details,
  hasBorder,
}) => (
  <div
    className={`split-image-body-details-panel border relative flex flex-col md:flex-row justify-center w-screen ${
      hasBorder ? 'border' : ''
    }`}
  >
    {/* Responsive Image */}
    <div className='md:hidden h-full w-full'>
      <Image {...image} isNative objectFit='cover' width='100%' height='100%' />
    </div>
    {/* Desktop & Tablet Image */}
    <div className='hidden absolute w-full left-0 md:block md:w-1/2 md:h-full overflow-hidden'>
      <Image {...image} isNative width='100%' height='100%' objectFit='cover' />
    </div>
    {/* Desktop Background */}
    <div className='relative md:absolute w-full h-full right-0 md:w-1/2 bg-gradient-bluelight' />
    {/* Body */}
    <div className='main-wrapper bg-gradient-bluelight md:bg-none md:mt-20 flex md:justify-end relative w-full md:border-t border-white text-white py-10'>
      <div className='w-full my-10 h-full border-t border-white md:border-t-0 md:pt-6 md:pl-10 md:my-0 lg:pt-12 lg:pl-20 md:w-1/2'>
        <div className='mt-10 md:mt-0'>
          <Body {...body} />
        </div>
        {details.items.map((detail, index) => {
          const { label, icon } = formatContentfulComponentProps(detail);
          return (
            <div
              className={`flex items-center pb-5 ${index === 0 ? 'pt-5' : ''}`}
            >
              <div className='w-14'>
                <Icon {...icon} />
              </div>
              <div className='ml-4'>
                <Label {...label} weight='bold' />
                <Label {...body} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);
